import i18n from 'i18n-js';
import { Alert, ImageUpload } from '@helpers';
import { checkEvent } from '@reducers/utils';
import { getNCFileById } from '@reducers/manufacture';
import { setNcFileData } from '@reducers/details';
import { IMAGE_UPLOAD_FOOTER_TEXT, IMAGE_UPLOAD_HEADER_TEXT, IMAGE_UPLOAD_MAIN_TEXT } from '@v2/pages/common/constants/base.constants';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { UseUploadNcFileVersionParams } from '@components/ncFileVersions/types';

export const useUploadNcFileVersion = ({ ncFile }: UseUploadNcFileVersionParams) => {
  const dispatch = useAppDispatch();

  const onUploadNewVersion = () => {
    if (!ncFile?.ncFileName || !ncFile?.ncFileId) return;

    const onUploadStart = () => {
      const versionsBefore = ncFile?.versions ?? [];
      let handled = false;

      Alert({
        title: i18n.t('Uploading new NC file version'),
        text: i18n.t('Processing has started. You can continue using the app — we will notify you when it is complete.'),
        loadingIndicator: true,
        actions: [{ text: i18n.t('Dismiss'), style: 'secondary' }],
      });

      checkEvent('OnStartedDocumentProcess', {
        timeout: 30000,
        connectionName: 'appHub',
        matcher: (eventData: { fileName?: string; documentType?: string }) =>
          eventData?.fileName === ncFile.ncFileName && eventData?.documentType === 'NcFile',
      })
        .then((eventData: { contentHash?: string }) => {
          const uploadedHash = eventData?.contentHash;
          if (!uploadedHash) return;

          const match = versionsBefore.find((v) => v.fileContentHash?.toLowerCase() === uploadedHash.toLowerCase());
          if (!match) return;

          handled = true;
          Alert({
            title: i18n.t('NC File Version Already Exists'),
            text: i18n.t('The uploaded file has identical content to Version {{version}}. No new version was created.', {
              version: match.sequenceNumber,
            }),
            actions: [{ text: i18n.t('OK'), style: 'primary' }],
          });
        })
        .catch(() => {
          // Non-fatal — OnProcessedNcFileDocument drives the final UX.
        });

      checkEvent('OnProcessedNcFileDocument', {
        timeout: 30000,
        connectionName: 'appHub',
        matcher: (eventData: { ncFile?: { ncFileId?: number; ncFileName?: string } }) =>
          eventData?.ncFile?.ncFileId === ncFile.ncFileId,
      })
        .then(async () => {
          if (handled) return;
          handled = true;

          const updatedNcFile = await dispatch(getNCFileById(ncFile.ncFileId));
          if (updatedNcFile) {
            dispatch(setNcFileData(updatedNcFile));
          }

          Alert({
            title: i18n.t('NC File version uploaded'),
            text: i18n.t('A new version of {{fileName}} has been processed successfully.', { fileName: ncFile.ncFileName }),
            actions: [{ text: i18n.t('OK'), style: 'primary' }],
          });
        })
        .catch(() => {
          if (handled) return;
          handled = true;
          Alert({ title: i18n.t('NC file processing timed out'), type: 'danger' });
        });
    };

    ImageUpload({
      addingNcFile: true,
      noPhoto: true,
      headerText: IMAGE_UPLOAD_HEADER_TEXT.AddNcFileVersion,
      headerDescription: ncFile.ncFileName,
      mainText: IMAGE_UPLOAD_MAIN_TEXT.NcFile,
      footerText: IMAGE_UPLOAD_FOOTER_TEXT.AddNcFile,
      documentType: 'NcFile',
      withoutAddExisting: true,
      uploadStart: onUploadStart,
      uploadEnd: () => {},
      tags: [
        {
          scopeId: 4, // NC File scope
          scopeReferenceId: ncFile.ncFileId,
          scopeReferenceName: ncFile.ncFileName,
        },
      ],
      expectedFileName: ncFile.ncFileName, // For exact name validation
    });
  };

  return { onUploadNewVersion };
};
